import React, { useState } from 'react';
import {
  AppBar,
  styled,
  TextField,
  InputAdornment,
  IconButton,
  Typography,
  Box,
  Divider,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewListIcon from '@mui/icons-material/ViewList';
import bmpoLogo from 'assets/img/BMPO_header_logo.png';
import bannerMapImage from 'assets/img/landingPageBannerMap.png';
import { useNavigate } from 'react-router-dom';
import { ROUTE_PATHS } from 'routes';
import DirectionsTransitFilledOutlinedIcon from '@mui/icons-material/DirectionsTransitFilledOutlined';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import AddRoadOutlinedIcon from '@mui/icons-material/AddRoadOutlined';
import TrafficOutlinedIcon from '@mui/icons-material/TrafficOutlined';
import EditCalendarOutlinedIcon from '@mui/icons-material/EditCalendarOutlined';
import LocalAtmOutlinedIcon from '@mui/icons-material/LocalAtmOutlined';
import PavementManagementIcon from 'assets/customIcons/PavementManagement';
import EmergencyResponsePlanningIcon from 'assets/customIcons/EmergecyResponsePlanningIcon';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';

export const StyledAppBar = styled(AppBar)(({ theme }) => ({
  zIndex: theme.zIndex.drawer + 1,
  backgroundColor: '#fff',
  boxShadow:
    '0px 1px 14px 0px rgba(0, 0, 0, 0.12), 0px 5px 8px 0px rgba(0, 0, 0, 0.14), 0px 3px 5px -1px rgba(0, 0, 0, 0.20)',
  height: '56px',
  padding: '1px 24px',
  '.MuiToolbar-root': {
    padding: '0px',
    height: '100%',
  },
}));

const modules = [
  {
    id: 1,
    title: 'Land Use Planning',
    icon: <BusinessOutlinedIcon />,
    desc: 'Scenario Planning to support economic development and community growth',
  },
  {
    id: 2,
    title: 'Affordable Housing',
    icon: <HomeOutlinedIcon />,
    desc: 'Address housing demands to support affordable and accessible living options',
  },
  {
    id: 0,
    title: 'Transportation Network Planning',
    icon: <DirectionsTransitFilledOutlinedIcon />,
    desc: 'Multi-modal transportation network to support planned regional growth and travel demand',
    disabled: true,
  },
  {
    id: 3,
    title: 'Road Safety',
    icon: <AddRoadOutlinedIcon />,
    desc: 'Prioritize Safety hotspots and identify countermeasures to enhance road safety',
  },
  {
    id: 4,
    title: 'Traffic Mobility Insights',
    icon: <TrafficOutlinedIcon />,
    desc: 'Real-time Traffic Monitoring and Congestion Management to enhance transportation mobility',
  },
  {
    id: 5,
    title: 'Emergency Response Planning',
    icon: <EmergencyResponsePlanningIcon />,
    desc: 'Effective response planning for transportation disruptions and extreme weather emergencies',
  },
  {
    id: 6,
    title: 'Event Planning',
    icon: <EditCalendarOutlinedIcon />,
    desc: 'Transportation Network and Traffic Management to ensure smooth event-day operations',
  },
  {
    id: 7,
    title: 'Pavement Management',
    icon: <PavementManagementIcon />,
    desc: 'Monitor and maintain road conditions for long-term infrastructure health',
  },
  {
    id: 8,
    title: 'Project Cost Estimation',
    icon: <LocalAtmOutlinedIcon />,
    desc: 'Accurately predict transportation project costs to optimize budgeting',
  },
];

function LandingPage() {
  const navigate = useNavigate();
  const [gridView, setGridView] = useState(true);

  const [searchText, setSearchText] = useState('');

  const filteredModules = modules.filter(
    (mod) =>
      mod.title.toLowerCase().includes(searchText.toLowerCase()) ||
      mod.desc.toLowerCase().includes(searchText.toLowerCase())
  );

  const handleModuleClick = (idx) => {
    if (idx >= modules.length - 2) return;
    if (idx === 0 || idx === 1 || idx === 2 || idx === 3 ) {
      navigate(ROUTE_PATHS.LOGIN);
    } else if (idx === 4 || idx === 5 || idx === 6) {
      window.location.href = 'https://iip-tmi-demo.web.app/dashboard';
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#fafbfd',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <StyledAppBar
        component='header'
        position='relative'
        sx={{ '.MuiToolbar-regular img': { width: 'auto' } }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <img
            src={bmpoLogo}
            alt='BMPO'
            style={{ height: '13.54px', cursor: 'pointer' }}
            onClick={() => {
              navigate(ROUTE_PATHS.DEFAULT);
            }}
          />
        </div>
      </StyledAppBar>
      <div
        style={{
          position: 'relative',
          width: '100%',
          height: 168,
          padding: '32px 48px',
          display: 'flex',
          alignItems: 'flex-start',
          background: 'linear-gradient(90deg, #F6F2E9 12.25%, #C0F0D3 41.42%)',
          gap: 4,
          borderBottom: '1px solid #D0D0CE',
          overflow: 'hidden',
        }}
      >
        <div style={{ flex: 1, zIndex: 1 }}>
          <div
            style={{
              fontWeight: 600,
              fontSize: 20,
              marginBottom: 8,
              color: '#181818',
            }}
          >
            Infrastructure Solutions
          </div>
          <div
            style={{
              fontWeight: 400,
              fontSize: 15,
              color: '#595959',
              lineHeight: '22px',
              letterSpacing: '0.01em',
              maxWidth: 900,
            }}
          >
            Comprehensive analysis and insights for land use planning, traffic mobility,
            and vulnerable road user safety. Enhance infrastructure, optimize traffic
            flow, and safeguard pedestrians and cyclists with cutting edge monitoring and
            strategic planning.
          </div>
        </div>
        <img
          src={bannerMapImage}
          alt='Map'
          style={{
            width: 393.562,
            height: 170,
            aspectRatio: '393.56/170.00',
            position: 'absolute',
            right: 0,
            bottom: -2,
            zIndex: 2,
            objectFit: 'cover',
          }}
        />
      </div>

      <Box
        sx={{
          width: '100%',
          marginTop: '32px',
          padding: { xs: '0 12px 0 12px', md: '0 32px 0 32px' },
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            marginBottom: '22px',
            background: '#fafbfd',
            zIndex: 1,
          }}
        >
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: '22px',
              lineHeight: '27px',
              letterSpacing: '-0.01em',
              color: '#181818',
              marginBottom: { xs: '15px', sm: '0' },
            }}
          >
            Modules
          </Typography>
          <Box display='flex' alignItems='center'>
            <TextField
              size='small'
              variant='outlined'
              placeholder='Find projects by name, topic, or username'
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              InputProps={{
                sx: {
                  fontSize: '15px',
                  height: 40,
                  paddingRight: '10px',
                  color: '#355174',
                  background: '#fff',
                },
                startAdornment: (
                  <InputAdornment position='start'>
                    <SearchIcon fontSize='small' htmlColor='#002547' />
                  </InputAdornment>
                ),
              }}
              sx={{
                minWidth: { xs: 200, sm: 320, md: 380 },
                '& .MuiOutlinedInput-root': {
                  borderRadius: '6px',
                  bgcolor: '#fff',
                  '& fieldset': {
                    borderColor: '#CED4DA',
                  },
                  '&:hover fieldset': {
                    borderColor: '#9DB3C5',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#1976d2',
                  },
                },
                '& input::placeholder': {
                  color: '#71819D',
                  opacity: 1,
                  fontWeight: 400,
                },
              }}
            />

            <Divider
              orientation='vertical'
              flexItem
              sx={{
                borderColor: '#E0E2E7',
                mx: 1.5,
                height: 34,
              }}
            />

            <Box
              sx={{
                display: 'flex',
                borderRadius: '6px',
                boxShadow: 'none',
                border: '1px solid #CED4DA',
                overflow: 'hidden',
                height: 40,
                width: 100,
              }}
            >
              <IconButton
                disableRipple
                onClick={() => setGridView(true)}
                sx={{
                  borderRadius: 0,
                  width: 40,
                  height: 40,
                  bgcolor: gridView ? '#005ea6' : '#F7F7F8',
                  color: gridView ? '#FFF' : '#71819D',
                  borderRight: '1px solid #CED4DA',
                  '&:hover': {
                    bgcolor: gridView ? '#005ea6' : '#ececec',
                  },
                }}
              >
                <ViewModuleIcon fontSize='small' />
              </IconButton>
              <IconButton
                disableRipple
                onClick={() => setGridView(false)}
                sx={{
                  borderRadius: 0,
                  width: 40,
                  height: 40,
                  bgcolor: !gridView ? '#005ea6' : '#F7F7F8',
                  color: !gridView ? '#FFF' : '#71819D',
                  '&:hover': {
                    bgcolor: !gridView ? '#005ea6' : '#ececec',
                  },
                }}
              >
                <ViewListIcon fontSize='small' />
              </IconButton>
            </Box>
          </Box>
        </Box>
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            pb: 3,
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
              gap: '20px',
            }}
          >
            {filteredModules.length === 0 ? (
              <Typography
                sx={{
                  gridColumn: '1 / -1',
                  textAlign: 'center',
                  color: '#777',
                  fontSize: 18,
                  p: 4,
                }}
              >
                No modules found.
              </Typography>
            ) : (
              filteredModules.map((mod) => {
                const origIdx = modules.findIndex((m) => m.id === mod.id);
                const isDisabled = origIdx >= modules.length - 2 || !!mod.disabled;

                return (
                  <Box
                    key={mod.id}
                    onClick={() => {
                      if (!isDisabled) handleModuleClick(origIdx);
                    }}
                    sx={{
                      background: isDisabled ? '#f5f5f5' : '#fff',
                      border: '1px solid #E0E2E7',
                      boxShadow: isDisabled
                        ? 'none'
                        : '0px 4px 8px 0px rgba(0, 0, 0, 0.04)',
                      borderRadius: '8px',
                      padding: '16px',
                      minHeight: '137px',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'flex-start',
                      transition: 'box-shadow 0.15s',
                      opacity: isDisabled ? 0.5 : 1,
                      pointerEvents: isDisabled ? 'none' : 'auto',
                      cursor: isDisabled ? 'not-allowed' : 'pointer',
                      '&:hover': !isDisabled
                        ? { boxShadow: '0 6px 18px 0 rgb(16 22 40 / 10%)' }
                        : {},
                    }}
                  >
                    <Box
                      sx={{
                        fontSize: 24,
                        color: isDisabled ? '#ccc' : '#474747',
                        marginBottom: 1,
                        display: 'flex',
                      }}
                    >
                      {mod.icon}
                    </Box>
                    <Typography
                      variant='subtitle2'
                      component='div'
                      sx={{
                        color: isDisabled ? '#888' : '#0054A4',
                        fontWeight: 500,
                        textDecoration: 'none',
                        fontSize: '15.5px',
                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                        '&:hover': !isDisabled ? { textDecoration: 'underline' } : {},
                        userSelect: 'none',
                      }}
                    >
                      {mod.title}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '14px',
                        color: isDisabled ? '#b1b4bc' : '#434751',
                        marginBottom: '14px',
                      }}
                    >
                      {mod.desc}
                    </Typography>
                  </Box>
                );
              })
            )}
          </Box>
        </Box>
      </Box>
    </div>
  );
}

export default LandingPage;
